import type { OverrideOptionInput } from '@erp/rpc';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  effectiveQuantity,
  isQuantityEditable,
  quantityDiff,
} from '../utils/lineQuantity';
import { roundQuantity, spreadNeedQuantity } from '../utils/needQuantitySpread';
import { classifyLine } from '../utils/lineGroups';
import {
  buildOverflowLineInput,
  buildQuantityOverride,
} from './quantityOverride';

export type NeedQuantityPlan =
  /** Not a number, or the total the need row already shows. */
  | { kind: 'unchanged' }
  /** Below 1 per editable SO line; `minimum` is the smallest total that fits. */
  | { kind: 'too-low'; minimum: number }
  /**
   * Above a special order's SO lines: the product is for those orders only,
   * so no stock line takes the rest. `maximum` is the SO lines' total.
   */
  | { kind: 'too-high'; maximum: number }
  | { kind: 'missing-context' }
  | {
      kind: 'save';
      saves: OverrideOptionInput[];
      /** The plan of a new stock line for the overflow, if there is one. */
      overflowLine: OverrideOptionInput | null;
      /** Stock lines a total below the SO lines leaves nothing for. */
      trash: string[];
      lineCount: number;
    };

/** The need row's quantity: the sum of its open SO lines. */
export function needQuantity(open: readonly AssignmentLine[]): number {
  return roundQuantity(
    open.reduce((total, line) => total + effectiveQuantity(line), 0),
  );
}

/**
 * The quantity overrides a typed need-row total saves (GEN-2938). Open lines
 * whose quantity can't be edited keep theirs and count toward the total; the
 * rest is spread over the editable ones (see spreadNeedQuantity). Lines whose
 * quantity doesn't change are not saved. An overflow goes on a new stock line
 * planned like the newest SO line. A total too low to leave each stock line 1
 * after the SO lines trashes the stock lines and spreads over the SO lines
 * alone. A special order takes no overflow.
 */
export function planNeedQuantitySaves({
  open,
  value,
  supplierId,
  sourceWarehouseId,
}: {
  /** The need's open lines: SO lines oldest first, then stock lines. */
  open: readonly AssignmentLine[];
  value: string;
  supplierId?: string;
  sourceWarehouseId?: string;
}): NeedQuantityPlan {
  // An empty draft is no total, not 0.
  const total = value.trim() === '' ? Number.NaN : Number(value);
  if (!Number.isFinite(total) || total === needQuantity(open)) {
    return { kind: 'unchanged' };
  }
  const editable = open.filter(isQuantityEditable);
  const fixed = needQuantity(open.filter((line) => !isQuantityEditable(line)));
  const maximum = specialOrderMaximum(open, editable, fixed);
  if (maximum != null && total > maximum) {
    return { kind: 'too-high', maximum };
  }
  const trash = stockLinesToTrash(editable, total - fixed);
  const trashed = new Set(trash);
  const spreadOver = editable.filter((line) => !trashed.has(line.suggestionId));
  const spread = spreadNeedQuantity(
    spreadOver.map((line) => ({
      suggestionId: line.suggestionId,
      calculated: quantityDiff(line).calculated,
      stock: classifyLine(line) === 'stock',
    })),
    total - fixed,
  );
  if (spread == null) {
    return {
      kind: 'too-low',
      minimum: roundQuantity(fixed + spreadOver.length),
    };
  }
  const context = { supplierId, sourceWarehouseId };
  const saves: OverrideOptionInput[] = [];
  for (const line of spreadOver) {
    const result = buildQuantityOverride({
      ...context,
      line,
      value: String(spread.quantities.get(line.suggestionId)),
    });
    if (result.kind === 'missing-context') return { kind: 'missing-context' };
    if (result.kind === 'save') saves.push(result.input);
  }
  const overflowLine = planOverflowLine(spreadOver, spread.overflow, context);
  if (overflowLine === 'missing-context') return { kind: 'missing-context' };
  return saves.length === 0 && overflowLine === null && trash.length === 0
    ? { kind: 'unchanged' }
    : {
        kind: 'save',
        saves,
        overflowLine,
        trash,
        lineCount: spreadOver.length,
      };
}

/**
 * A special order's highest total (GEN-2938): its SO lines' calculated
 * quantities, plus any stock line it already has at its current quantity, so
 * a raise never adds stock. Null for any other need.
 */
function specialOrderMaximum(
  open: readonly AssignmentLine[],
  editable: readonly AssignmentLine[],
  fixed: number,
): number | null {
  if (!open.some((line) => classifyLine(line) === 'potf')) return null;
  const stock = editable.filter((line) => classifyLine(line) === 'stock');
  const so = editable.filter((line) => classifyLine(line) !== 'stock');
  return roundQuantity(fixed + calculatedTotal(so) + needQuantity(stock));
}

function calculatedTotal(lines: readonly AssignmentLine[]): number {
  return roundQuantity(
    lines.reduce((sum, line) => sum + quantityDiff(line).calculated, 0),
  );
}

/**
 * The editable stock lines to trash for `total` (GEN-2938): all of them when
 * the SO lines' calculated quantities leave less than 1 for each, so a total
 * lowered below the SO lines comes off stock first, then the SO lines.
 */
function stockLinesToTrash(
  editable: readonly AssignmentLine[],
  total: number,
): string[] {
  const stock = editable.filter((line) => classifyLine(line) === 'stock');
  const so = editable.filter((line) => classifyLine(line) !== 'stock');
  if (stock.length === 0 || so.length === 0) return [];
  return total < calculatedTotal(so) + stock.length
    ? stock.map((line) => line.suggestionId)
    : [];
}

function planOverflowLine(
  editable: readonly AssignmentLine[],
  overflow: number,
  context: { supplierId?: string; sourceWarehouseId?: string },
): OverrideOptionInput | null | 'missing-context' {
  const newest = editable.findLast((line) => classifyLine(line) !== 'stock');
  if (overflow <= 0 || newest == null) return null;
  const result = buildOverflowLineInput({
    ...context,
    line: newest,
    quantity: overflow,
  });
  if (result.kind === 'missing-context') return 'missing-context';
  return result.kind === 'save' ? result.input : null;
}
